import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { InstrumentResource, OrderManagementResource } from '../../resources';
import { Client, ordersAtom } from '../../state';
import {
  ExpiryType,
  getUnknownListedInstrument,
  ListedInstrument,
  Order,
  OrderStateEnum,
  OrderType,
  SelectedAccountOrClient,
  TradeType,
  TradingSession,
} from '../../types';
import { isDefined, Loadable, LoadableState } from '../../utils';
import {
  GetResourceHeaderForMultiAccount,
  GetResourceHeaderWithToken,
  ResourceHeadersOfMultiAccountRequest,
} from '../useResource';

export type OpenOrdersCommonParams = {
  selectedClient: Client | undefined;
  selectedAccountOrClient: SelectedAccountOrClient | undefined;
  instrumentResource: InstrumentResource;
  orderManagementResource: OrderManagementResource;
  getResourceHeaderWithToken: GetResourceHeaderWithToken;
  getResourceHeaderForMultiAccount: GetResourceHeaderForMultiAccount;
};

export type OpenOrder = {
  id: string;
  instrument: ListedInstrument;
  tradeType: TradeType;
  quantity: number;
  // market order price is empty
  price?: number;
  commission: number | null;
  orderValue?: number;
  stopPrice?: number;
  orderType: OrderType;
  expiryType: ExpiryType;
  state: OrderStateEnum;
  accountId?: string;
  filledQty?: number;
  tradingSession?: TradingSession;
};

export type OpenOrders = {
  openOrders: Array<OpenOrder>;
};

const mapOrderToOpenOrder = (orders: Array<Order>): Array<OpenOrder> =>
  orders.map<OpenOrder>(order => {
    const openOrder: OpenOrder = {
      id: order.id,
      instrument: order.instrument,
      tradeType: order.direction,
      quantity: order.quantity,
      price: order.price,
      commission: order.commission ?? null,
      orderValue: order.orderValue,
      stopPrice: order.stopActivationPrice,
      orderType: order.orderType,
      expiryType: order.expiryType,
      state: order.state,
      accountId: order.accountId,
      filledQty: order.filledQty,
      tradingSession: order.tradingSession,
    };

    return openOrder;
  });

export const useOpenOrdersCommon = ({
  instrumentResource,
  orderManagementResource,
  selectedAccountOrClient,
  selectedClient,
  getResourceHeaderWithToken,
  getResourceHeaderForMultiAccount,
}: OpenOrdersCommonParams) => {
  const [orders, setOrders] = useAtom(ordersAtom);

  const accountIdsOfSelectedClient = useMemo(
    () => selectedClient?.accounts.map(account => account.accountNumber),
    [selectedClient],
  );

  // Helps filtering open orders for the selected account/client.
  const openOrdersPredicate = useMemo<(order: OpenOrder) => boolean>(() => {
    if (isDefined(selectedAccountOrClient?.account)) {
      return (order: OpenOrder): boolean => order.accountId === selectedAccountOrClient.account?.accountNumber;
    }
    if (isDefined(selectedAccountOrClient?.client)) {
      return (order: OpenOrder): boolean => Boolean(accountIdsOfSelectedClient?.includes(order.accountId!));
    }
    return () => false;
  }, [accountIdsOfSelectedClient, selectedAccountOrClient]);

  const openOrdersForSelectedClient = useMemo<LoadableState<OpenOrder[]>>(
    () => Loadable.map(orders, orders => mapOrderToOpenOrder(orders.orders)),
    [orders],
  );

  const openOrders = useMemo<LoadableState<OpenOrders>>(
    () => Loadable.map(openOrdersForSelectedClient, orders => ({ openOrders: orders.filter(openOrdersPredicate) })),
    [openOrdersForSelectedClient, openOrdersPredicate],
  );

  /** We want to load the orders for all the accounts in the selected client. */
  const getResourceHeadersForAllAccountsInSelectedClient = useCallback((): ResourceHeadersOfMultiAccountRequest => {
    const headers = getResourceHeaderForMultiAccount();
    return {
      ...headers,
      ...(isDefined(accountIdsOfSelectedClient) ? { accountId: accountIdsOfSelectedClient } : {}),
    };
  }, [accountIdsOfSelectedClient, getResourceHeaderForMultiAccount]);

  const fetchOrders = useCallback(async () => {
    const response = await orderManagementResource.getOrders({}, getResourceHeadersForAllAccountsInSelectedClient());

    if (response.data.length === 0) {
      return [];
    }

    const instruments = await instrumentResource.batchInstrumentQuery(
      { isins: response.data.map(order => order.isin) },
      getResourceHeaderWithToken(),
    );

    const orders: Array<Order> = response.data.map<Order>(order => ({
      ...order,
      instrument:
        instruments.data.find(instrument => instrument.isin === order.isin && order.mic === instrument.mic) ||
        getUnknownListedInstrument(order.isin, order.isin),
    }));

    return orders;
  }, [
    orderManagementResource,
    getResourceHeadersForAllAccountsInSelectedClient,
    instrumentResource,
    getResourceHeaderWithToken,
  ]);

  const loadOrders = useCallback(
    async (clear: boolean = true) => {
      if (clear) {
        setOrders(Loadable.loading);
      } else {
        setOrders(curr => {
          if (curr.isLoaded || (curr.isLoading && curr.hasValue)) {
            return Loadable.loadingWith(curr);
          }

          return Loadable.loading;
        });
      }

      try {
        setOrders(Loadable.loaded({ orders: await fetchOrders() }));
      } catch (error) {
        setOrders(Loadable.failed);
        throw error;
      }
    },
    [setOrders, fetchOrders],
  );

  const cancelAllOrders = useCallback(async () => {
    await orderManagementResource.cancelAllOrders(getResourceHeaderForMultiAccount());
    loadOrders(false);
  }, [orderManagementResource, loadOrders, getResourceHeaderForMultiAccount]);

  const cancelOrder = useCallback(
    async (orderId: string, accountId?: string) => {
      await orderManagementResource.cancelOrder(
        orderId,
        accountId ? { ...getResourceHeaderWithToken(), accountId } : getResourceHeaderWithToken(),
      );
      setOrders(curr => {
        if (curr.isLoaded) {
          const filteredOrders = { ...curr, orders: curr.orders.filter(order => order.id !== orderId) };
          return Loadable.loaded(filteredOrders);
        }
        return curr;
      });
    },
    [orderManagementResource, getResourceHeaderWithToken, setOrders],
  );

  const getOrderById = useCallback(
    (orderId: string) => {
      if (orders.isLoaded) {
        return orders.orders.find(order => order.id === orderId);
      }

      return undefined;
    },
    [orders],
  );

  return useMemo(
    () => ({
      openOrders,
      openOrdersForSelectedClient,
      loadOrders,
      cancelAllOrders,
      cancelOrder,
      getOrderById,
    }),
    [openOrders, openOrdersForSelectedClient, loadOrders, cancelAllOrders, cancelOrder, getOrderById],
  );
};
